const MID_CHAR = 'n';
const MIN_CHAR_CODE = 'a'.charCodeAt(0);
const MAX_CHAR_CODE = 'z'.charCodeAt(0);

export class RankExhausted extends Error {
  constructor(prev: string, next: string) {
    super(`rank exhausted between "${prev}" and "${next}"`);
    this.name = 'RankExhausted';
  }
}

export class RankInvalid extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RankInvalid';
  }
}

const MAX_RANK_LENGTH = 64;

const incrementRank = (a: string): string => {
  if (a.length >= MAX_RANK_LENGTH) {
    throw new RankExhausted(a, '');
  }
  return a + MID_CHAR;
};

const decrementRank = (b: string): string => {
  if (b === '') {
    throw new RankInvalid('cannot decrement empty rank');
  }
  let i = 0;
  while (i < b.length && b.charCodeAt(i) === MIN_CHAR_CODE) i++;
  if (i === b.length) {
    throw new RankExhausted('', b);
  }
  const bv = b.charCodeAt(i);
  if (bv - MIN_CHAR_CODE >= 2) {
    const mid = MIN_CHAR_CODE + Math.floor((bv - MIN_CHAR_CODE) / 2);
    return b.slice(0, i) + String.fromCharCode(mid);
  }
  if (b.slice(0, i).length + 2 >= MAX_RANK_LENGTH) {
    throw new RankExhausted('', b);
  }
  return b.slice(0, i) + 'a' + MID_CHAR;
};

export const rankBetween = (prev?: string, next?: string): string => {
  const a = prev ?? '';
  const b = next ?? '';

  if (a === '' && b === '') return MID_CHAR;
  if (a === '') return decrementRank(b);
  if (b === '') return incrementRank(a);
  if (a >= b) throw new RankInvalid(`prev "${a}" must be < next "${b}"`);

  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i++;

  if (i === a.length) {
    return a + decrementRank(b.slice(i));
  }

  const av = a.charCodeAt(i);
  const bv = b.charCodeAt(i);

  if (bv - av >= 2) {
    const mid = av + Math.floor((bv - av) / 2);
    return a.slice(0, i) + String.fromCharCode(mid);
  }

  return a.slice(0, i + 1) + incrementRank(a.slice(i + 1));
};

export const compareRanks = (a: string, b: string): number => {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
};

export const REBALANCE_RANKS = (count: number): string[] => {
  if (count <= 0) return [];
  const span = MAX_CHAR_CODE - MIN_CHAR_CODE + 1;
  const step = span / (count + 1);
  const ranks: string[] = [];
  for (let i = 1; i <= count; i++) {
    const code = MIN_CHAR_CODE + Math.floor(step * i);
    ranks.push(String.fromCharCode(Math.min(code, MAX_CHAR_CODE)));
  }
  for (let i = 1; i < ranks.length; i++) {
    if (ranks[i] <= ranks[i - 1]) {
      ranks[i] = ranks[i - 1] + MID_CHAR;
    }
  }
  return ranks;
};
